import type {Values} from '@wix/devzai-utils-common';
import {hashFunctionCreate} from '@wix/devzai-utils-common';
import type {DoppeDtoUserMetaSiteType} from './doppe-dto-user';
import type {DoppeDtoStoreProduct} from './doppe-dto-store-product';
import type {DoppeDtoBlogPost} from './doppe-dto-blog-post';
import type {DoppeDtoBookingService} from './doppe-dto-booking-service';
import type {DoppeDtoVideo} from './doppe-dto-video';
import type {DoppeDtoCoupon} from './doppe-dto-coupon';
import type {DoppeDtoEvent} from './doppe-dto-event';
import type {DoppeDtoMembershipPlan} from './doppe-dto-membership-plan';
import type {DoppeDtoExternalForm} from '../doppe-external-form/doppe-external-form';

export const DoppeBusinessEntity = {
    WixProduct: 'WixProduct',
    WixAutoProductsList: 'WixAutoProductsList',
    WixBlogPost: 'WixBlogPost',
    WixAutoBlogPost: 'WixAutoBlogPost',
    WixAutoBlogPostsList: 'WixAutoBlogPostsList',
    WixService: 'WixService',
    WixAutoServicesList: 'WixAutoServicesList',
    WixCoupon: 'WixCoupon',
    WixVideo: 'WixVideo',
    WixEvent: 'WixEvent',
    WixAutoEventsList: 'WixAutoEventsList',
    WixAutoMembershipPlansList: 'WixAutoMembershipPlansList',
    WixMembershipPlan: 'WixMembershipPlan',
    /** Form from the user's linked Wix site. Reserved for future use. */
    WixForm: 'WixForm',
    HoppPricingPlan: 'HoppPricingPlan',
    HoppAutoPricingPlansList: 'HoppAutoPricingPlansList',
    HoppProduct: 'HoppProduct',
    HoppAutoProductsList: 'HoppAutoProductsList',
    HoppBlogPost: 'HoppBlogPost',
    HoppAutoBlogPostsList: 'HoppAutoBlogPostsList',
    HoppService: 'HoppService',
    HoppAutoServicesList: 'HoppAutoServicesList',
    HoppEvent: 'HoppEvent',
    HoppAutoEventsList: 'HoppAutoEventsList',
    HoppForm: 'HoppForm',
} as const;

export type DoppeBusinessEntity = Values<typeof DoppeBusinessEntity>;

export const DoppeAutoListMaxItems = 20;

export const DoppeAutoBlogPostsListMaxItems = 20;
export const DoppeAutoProductsListMaxItems = 20;
export const DoppeAutoMembershipPlansListMaxItems = 20;
export const DoppeAutoBookingServicesListMaxItems = 20;
export const DoppeAutoEventsListMaxItems = 20;

export const DoppeBusinessEntityDtoUnavailableReason = {
    FailedLoading: 'FailedLoading',
    NotFound: 'NotFound'
} as const;

export type DoppeBusinessEntityDtoUnavailableReason = Values<typeof DoppeBusinessEntityDtoUnavailableReason>;

export type DoppeBusinessEntityReferenceMetadata = {
    categoryIds?: string[];
}

export type DoppeBusinessEntityReference<E extends DoppeBusinessEntity = DoppeBusinessEntity> = {
    businessEntity: E;
    id: string;
    metadata?: DoppeBusinessEntityReferenceMetadata;
}

export namespace DoppeBusinessEntityReference {
    export type ResolveBusinessEntity<R extends DoppeBusinessEntityReference> =
        R extends DoppeBusinessEntityReference<infer E> ? E : never;
}

type DoppeBusinessEntityDataBase<T extends DoppeBusinessEntity, DTO> = {
    id: string;
    type: T;
    dto: DTO;
    metadata?: DoppeBusinessEntityReferenceMetadata;
}

export type DoppeBusinessEntityDtoNotFound = {
    __notFound: true;
}

/**
 * Maps each list entity type to its corresponding single entity type.
 * Used to resolve individual items from list entities.
 */
export const DoppeBusinessEntityAutoListToSingleEntityMap = {
    [DoppeBusinessEntity.WixAutoProductsList]: DoppeBusinessEntity.WixProduct,
    [DoppeBusinessEntity.HoppAutoProductsList]: DoppeBusinessEntity.HoppProduct,
    [DoppeBusinessEntity.WixAutoBlogPostsList]: DoppeBusinessEntity.WixBlogPost,
    [DoppeBusinessEntity.HoppAutoBlogPostsList]: DoppeBusinessEntity.HoppBlogPost,
    [DoppeBusinessEntity.WixAutoServicesList]: DoppeBusinessEntity.WixService,
    [DoppeBusinessEntity.HoppAutoServicesList]: DoppeBusinessEntity.HoppService,
    [DoppeBusinessEntity.WixAutoEventsList]: DoppeBusinessEntity.WixEvent,
    [DoppeBusinessEntity.HoppAutoEventsList]: DoppeBusinessEntity.HoppEvent,
    [DoppeBusinessEntity.WixAutoMembershipPlansList]: DoppeBusinessEntity.WixMembershipPlan,
    [DoppeBusinessEntity.HoppAutoPricingPlansList]: DoppeBusinessEntity.HoppPricingPlan,
} as const;

export type DoppeBusinessEntityAutoListToSingleEntityMap = typeof DoppeBusinessEntityAutoListToSingleEntityMap;

type DoppeBusinessEntityTypeToDto = {
    [DoppeBusinessEntity.WixProduct]: DoppeDtoStoreProduct | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixAutoProductsList]: string[];
    [DoppeBusinessEntity.WixAutoBlogPost]: DoppeDtoBlogPost | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixAutoBlogPostsList]: string[];
    [DoppeBusinessEntity.WixBlogPost]: DoppeDtoBlogPost | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixService]: DoppeDtoBookingService | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixAutoServicesList]: string[];
    [DoppeBusinessEntity.WixCoupon]: DoppeDtoCoupon | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixVideo]: DoppeDtoVideo | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixEvent]: DoppeDtoEvent | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixAutoEventsList]: string[];
    [DoppeBusinessEntity.WixMembershipPlan]: DoppeDtoMembershipPlan | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.WixAutoMembershipPlansList]: string[];
    /** Reserved for future use — form from the user's linked Wix site. */
    [DoppeBusinessEntity.WixForm]: DoppeDtoExternalForm | DoppeBusinessEntityDtoNotFound;

    [DoppeBusinessEntity.HoppProduct]: DoppeDtoStoreProduct | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.HoppAutoProductsList]: string[];
    [DoppeBusinessEntity.HoppBlogPost]: DoppeDtoBlogPost | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.HoppAutoBlogPostsList]: string[];
    [DoppeBusinessEntity.HoppService]: DoppeDtoBookingService | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.HoppAutoServicesList]: string[];
    [DoppeBusinessEntity.HoppEvent]: DoppeDtoEvent | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.HoppAutoEventsList]: string[];
    [DoppeBusinessEntity.HoppPricingPlan]: DoppeDtoMembershipPlan | DoppeBusinessEntityDtoNotFound;
    [DoppeBusinessEntity.HoppAutoPricingPlansList]: string[];
    [DoppeBusinessEntity.HoppForm]: DoppeDtoExternalForm | DoppeBusinessEntityDtoNotFound;
};

/** Maps a list entity type to the DTO type of its individual items. */
type DoppeBusinessEntityAutoListItemDto = {
    [K in keyof DoppeBusinessEntityAutoListToSingleEntityMap]:
        Exclude<DoppeBusinessEntityTypeToDto[DoppeBusinessEntityAutoListToSingleEntityMap[K]], DoppeBusinessEntityDtoNotFound>
};

export type DoppeBusinessEntityDataByType = {
    [K in keyof DoppeBusinessEntityTypeToDto]: DoppeBusinessEntityDataBase<K, DoppeBusinessEntityTypeToDto[K]>
}

export type DoppeBusinessEntityData = Values<DoppeBusinessEntityDataByType>;

export namespace DoppeBusinessEntityData {
    export type ResolveDtoFromType<E extends DoppeBusinessEntity> = DoppeBusinessEntityTypeToDto[E];

    export type ResolveAvailableDtoFromType<E extends DoppeBusinessEntity> = Exclude<ResolveDtoFromType<E>, DoppeBusinessEntityDtoNotFound>

    export type ResolveFromType<E extends DoppeBusinessEntity> = DoppeBusinessEntityDataByType[E];

    /** Resolves the DTO type of individual items within a list entity. */
    export type ResolveListItemDtoFromType<E extends DoppeBusinessEntity> =
        E extends keyof DoppeBusinessEntityAutoListItemDto ? DoppeBusinessEntityAutoListItemDto[E] : never;
}

export type DoppeBusinessEntityDto = Values<DoppeBusinessEntityTypeToDto>;

const metadataHashFunction = hashFunctionCreate<DoppeBusinessEntityReferenceMetadata>({
    categoryIds: hashFunctionCreate(),
});

export const doppeBusinessEntityReferenceHashFunction = hashFunctionCreate<DoppeBusinessEntityReference>({
    businessEntity: hashFunctionCreate(),
    id: hashFunctionCreate(),
    metadata: hashFunctionCreate<DoppeBusinessEntityReferenceMetadata | undefined>(
        value => value ? metadataHashFunction(value) : '' as any
    ),
})

export function doppeBusinessEntityDataGetBusinessEntityReference(
    businessEntityData: DoppeBusinessEntityData
): DoppeBusinessEntityReference {
    return {
        businessEntity: businessEntityData.type,
        id: businessEntityData.id,
        metadata: businessEntityData.metadata,
    }
}

export function doppeBusinessEntityReferenceEqual(ref1: DoppeBusinessEntityReference, ref2: DoppeBusinessEntityReference) {
    return doppeBusinessEntityReferenceHashFunction(ref1) === doppeBusinessEntityReferenceHashFunction(ref2);
}

export function doppeBusinessEntityDtoCreateNotFound(): DoppeBusinessEntityDtoNotFound {
    return {
        __notFound: true
    }
}

export function doppeBusinessEntityDtoIsNotFound(businessEntityDto: DoppeBusinessEntityDto): businessEntityDto is DoppeBusinessEntityDtoNotFound {
    return (businessEntityDto as DoppeBusinessEntityDtoNotFound)?.__notFound === true;
}

export function doppeBusinessEntityDtoIsAvailable<E extends DoppeBusinessEntity>(businessEntityDto: DoppeBusinessEntityData.ResolveDtoFromType<E> | undefined): businessEntityDto is DoppeBusinessEntityData.ResolveAvailableDtoFromType<E> {
    return businessEntityDto !== undefined && !doppeBusinessEntityDtoIsNotFound(businessEntityDto);
}

/**
 * Checks if a business entity type is a Hopp-native entity (as opposed to a Wix entity).
 * Hopp entities don't have external URLs, so an empty link is expected and normal.
 */
export function doppeBusinessEntityIsHoppEntity(businessEntity: DoppeBusinessEntity): boolean {
    switch (businessEntity) {
        case DoppeBusinessEntity.HoppProduct:
        case DoppeBusinessEntity.HoppAutoProductsList:
        case DoppeBusinessEntity.HoppBlogPost:
        case DoppeBusinessEntity.HoppAutoBlogPostsList:
        case DoppeBusinessEntity.HoppService:
        case DoppeBusinessEntity.HoppAutoServicesList:
        case DoppeBusinessEntity.HoppEvent:
        case DoppeBusinessEntity.HoppAutoEventsList:
        case DoppeBusinessEntity.HoppPricingPlan:
        case DoppeBusinessEntity.HoppAutoPricingPlansList:
        case DoppeBusinessEntity.HoppForm:
            return true;
        default:
            return false;
    }
}

/** Resolves the MetaSite type (LinkedMetaSite vs CreatorsMetaSite) for a given business entity. */
export function doppeBusinessEntityResolveMetaSiteType(businessEntity: DoppeBusinessEntity): DoppeDtoUserMetaSiteType {
    return doppeBusinessEntityIsHoppEntity(businessEntity)
        ? 'CreatorsMetaSite' as const
        : 'LinkedMetaSite' as const;
}

/** Resolves the display label for the source of an auto-list business entity. */
export function doppeBusinessEntityAutoListResolveSourceLabel(
    businessEntity: DoppeBusinessEntity,
    locales: Record<string, string>
): string {
    switch (businessEntity) {
        case DoppeBusinessEntity.WixAutoProductsList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.wixStore'];
        case DoppeBusinessEntity.HoppAutoProductsList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.hoppStore'];
        case DoppeBusinessEntity.WixAutoEventsList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.wixEvents'];
        case DoppeBusinessEntity.HoppAutoEventsList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.hoppEvents'];
        case DoppeBusinessEntity.WixAutoBlogPostsList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.wixBlog'];
        case DoppeBusinessEntity.HoppAutoBlogPostsList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.hoppBlog'];
        case DoppeBusinessEntity.WixAutoServicesList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.wixServices'];
        case DoppeBusinessEntity.HoppAutoServicesList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.hoppServices'];
        case DoppeBusinessEntity.WixAutoMembershipPlansList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.wixPlans'];
        case DoppeBusinessEntity.HoppAutoPricingPlansList:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.hoppPlans'];
        default:
            return locales['actionsMetaData.lists.dataSource.fields.storeSource.values.manual'];
    }
}

/**
 * Resolves the single entity type for a given list entity type.
 * For example, WixAutoProductsList -> WixProduct, HoppAutoEventsList -> HoppEvent.
 */
export function doppeBusinessEntityAutoListResolveSingleEntityType<E extends keyof DoppeBusinessEntityAutoListToSingleEntityMap>(
    listEntityType: E
): DoppeBusinessEntityAutoListToSingleEntityMap[E] {
    return DoppeBusinessEntityAutoListToSingleEntityMap[listEntityType];
}
